import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/helpers';

const CosmicProfileWidget = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('12:00');
  const [birthPlace, setBirthPlace] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCities, setShowCities] = useState(false);

  useEffect(() => {
    fetch(`${API}/user/cosmic-profile`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.has_profile) setProfile(data);
        else setShowForm(true);
      })
      .catch(() => setShowForm(true))
      .finally(() => setLoading(false));
  }, []);

  const searchCities = async (query) => {
    if (query.length < 2) { setCitySuggestions([]); return; }
    try {
      const res = await fetch(`${API}/cities/search?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setCitySuggestions(data.cities || []);
      setShowCities(true);
    } catch (e) {}
  };

  const generate = async () => {
    if (!birthDate) { toast.error('Ingresa tu fecha de nacimiento'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/user/cosmic-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          birth_date: birthDate,
          birth_time: birthTime,
          birth_place: birthPlace,
          name: user?.name
        })
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setShowForm(false);
        toast.success('Perfil cosmico generado');
      }
    } catch (e) { toast.error('Error al generar perfil'); }
    setGenerating(false);
  };

  const zodiacSymbols = {
    aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋', leo: '♌', virgo: '♍',
    libra: '♎', scorpio: '♏', sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓'
  };

  if (loading) return (
    <div className="glass-card p-6 mb-8 border border-gold-300/10">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-gold-300 animate-pulse" />
        <span className="text-sm text-[var(--text-muted)]">Cargando perfil cosmico...</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[0,1,2,3].map(i => (
          <div key={i} className="text-center p-3 rounded-xl bg-white/5 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-white/5 mx-auto mb-2" />
            <div className="h-3 bg-white/5 rounded w-16 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="glass-card p-6 mb-8 border border-gold-300/10"
      data-testid="cosmic-profile-widget"
    >
      <h2 className="text-lg font-bold gold-text mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5" /> Perfil Cosmico
      </h2>

      {profile && !showForm ? (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Sol', sign: profile.sun_sign_es, key: profile.sun_sign, desc: profile.sun_description, color: 'text-yellow-400' },
              { label: 'Luna', sign: profile.moon_sign_es, key: profile.moon_sign, desc: profile.moon_description, color: 'text-blue-300' },
              { label: 'Ascendente', sign: profile.rising_sign_es, key: profile.rising_sign, desc: profile.rising_description, color: 'text-pink-400' },
              { label: `Camino ${profile.life_path}`, sign: `Numero ${profile.life_path}`, key: null, desc: profile.life_path_description, color: 'text-green-400' },
            ].map((item, i) => (
              <div key={i} className="text-center p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group cursor-default" title={item.desc}>
                <p className="text-3xl mb-1">{item.key ? (zodiacSymbols[item.key] || '✦') : '🔢'}</p>
                <p className={`text-sm font-bold ${item.color}`}>{item.sign}</p>
                <p className="text-xs text-[var(--text-muted)]">{item.label}</p>
              </div>
            ))}
          </div>
          {profile.cosmic_summary && (
            <p className="text-[var(--text-muted)] text-sm italic" data-testid="cosmic-summary">{profile.cosmic_summary}</p>
          )}
          <button onClick={() => setShowForm(true)} className="text-xs text-purple-400 underline mt-3" data-testid="recalculate-cosmic-btn">
            Recalcular perfil
          </button>
        </div>
      ) : (
        <div>
          <p className="text-[var(--text-muted)] text-sm mb-4">Ingresa tus datos de nacimiento para descubrir tu perfil cosmico completo.</p>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Fecha de nacimiento</label>
              <input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} className="mystic-input text-sm" data-testid="cosmic-birth-date" />
            </div>
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Hora de nacimiento</label>
              <input type="time" value={birthTime} onChange={e => setBirthTime(e.target.value)} className="mystic-input text-sm" data-testid="cosmic-birth-time" />
            </div>
            <div className="relative">
              <label className="block text-xs text-[var(--text-muted)] mb-1">Lugar de nacimiento</label>
              <input 
                type="text" value={birthPlace}
                onChange={e => { setBirthPlace(e.target.value); searchCities(e.target.value); }}
                onBlur={() => setTimeout(() => setShowCities(false), 200)}
                placeholder="Ciudad..."
                className="mystic-input text-sm"
                data-testid="cosmic-birth-place"
              />
              {showCities && citySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-[var(--bg-secondary)] border border-gold-300/10 rounded-lg max-h-40 overflow-auto">
                  {citySuggestions.map((city, i) => (
                    <button key={i} onClick={() => { setBirthPlace(city); setShowCities(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-purple-400/10"
                    >{city}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={generate} disabled={generating} className="btn-gold text-sm" data-testid="generate-cosmic-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Sparkles className="w-4 h-4 inline mr-2" />}
              {generating ? 'Calculando...' : 'Generar Perfil Cosmico'}
            </button>
            {profile && (
              <button onClick={() => setShowForm(false)} className="btn-ghost text-sm">Cancelar</button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CosmicProfileWidget;
